import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Sun, Moon, Mail, Phone } from 'lucide-react';
import { companyInfo } from '../data/mock';
import { useTheme } from '../context/ThemeContext';

const navLinks = [
  { name: "Home", href: "/" },
  { name: "About", href: "/about" },
  { name: "Services", href: "/services" },
  { name: "Careers", href: "/careers" },
  { name: "Contact", href: "/contact" }
];

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMenuOpen(false);
    window.scrollTo(0, 0);
  }, [location]);

  const isActive = (href) => {
    if (href === '/') return location.pathname === '/';
    return location.pathname.startsWith(href);
  };

  return (
    <>
      {/* Top Bar with Contact Info */}
      <div className={`hidden md:block w-full py-2 transition-colors duration-300 ${
        theme === 'dark' ? 'bg-[#0a0a0a] border-b border-white/10' : 'bg-gray-100 border-b border-gray-200'
      }`}>
        <div className="max-w-[1400px] mx-auto px-6 lg:px-[7.6923%] flex items-center justify-between">
          <div className="flex items-center gap-6">
            <a href={`mailto:${companyInfo.email}`} className={`flex items-center gap-2 text-sm transition-colors ${
              theme === 'dark' ? 'text-white/60 hover:text-[#00FFD1]' : 'text-gray-600 hover:text-[#00997A]'
            }`}>
              <Mail size={14} />
              {companyInfo.email}
            </a>
            <a href={`tel:${companyInfo.phone}`} className={`flex items-center gap-2 text-sm transition-colors ${
              theme === 'dark' ? 'text-white/60 hover:text-[#00FFD1]' : 'text-gray-600 hover:text-[#00997A]'
            }`}>
              <Phone size={14} />
              {companyInfo.phone}
            </a>
          </div>
          <div className={`text-sm ${theme === 'dark' ? 'text-white/40' : 'text-gray-500'}`}>
            {companyInfo.hours}
          </div>
        </div>
      </div>

      {/* Main Header */}
      <header 
        className={`sticky top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled 
            ? theme === 'dark' 
              ? 'bg-black/95 backdrop-blur-md border-b border-white/10' 
              : 'bg-white/95 backdrop-blur-md border-b border-gray-200 shadow-sm'
            : theme === 'dark' ? 'bg-black' : 'bg-white'
        }`}
      >
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-[7.6923%]">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo */}
            <Link to="/" className="flex flex-col items-start mt-[20px]">
              <img 
                src="https://customer-assets.emergentagent.com/job_67218e68-4bb7-4a39-b578-c9fa413502c7/artifacts/omsxniwo_Connecting%20Top%20IT%20Talents%20to%20Success.png" 
                alt="RKS Technologies" 
                className="h-[120px] sm:h-[150px] md:h-[180px] lg:h-[210px] w-auto object-contain"
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-6 xl:gap-8">
              {navLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.href}
                  className={`text-[16px] font-medium transition-colors duration-300 ${
                    isActive(link.href)
                      ? theme === 'dark' ? 'text-[#00FFD1]' : 'text-[#00997A]'
                      : theme === 'dark' 
                        ? 'text-white/60 hover:text-white' 
                        : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
            </nav>

            {/* Desktop Actions */}
            <div className="hidden lg:flex items-center gap-3">
              {/* Theme Toggle */}
              <button
                onClick={toggleTheme}
                className={`p-2.5 rounded-lg transition-colors ${
                  theme === 'dark' 
                    ? 'bg-white/10 hover:bg-white/20 text-white' 
                    : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                }`}
                aria-label="Toggle theme"
              >
                {theme === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
              </button>

              {/* CTA Button */}
              <Link
                to="/contact"
                className={`px-5 py-2.5 font-medium text-[16px] transition-all duration-300 ${
                  theme === 'dark'
                    ? 'bg-[#00FFD1] text-black hover:bg-[#00FFD1]/80'
                    : 'bg-[#00997A] text-white hover:bg-[#007A61]'
                }`}
              >
                Get Started
              </Link>
            </div>

            {/* Mobile Actions */}
            <div className="flex lg:hidden items-center gap-2">
              <button
                onClick={toggleTheme}
                className={`p-2 rounded-lg transition-colors ${
                  theme === 'dark' 
                    ? 'bg-white/10 text-white' 
                    : 'bg-gray-100 text-gray-700'
                }`}
                aria-label="Toggle theme"
              >
                {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
              </button>
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className={`p-2 transition-colors ${
                  theme === 'dark' ? 'text-white hover:text-[#00FFD1]' : 'text-gray-700 hover:text-[#00997A]'
                }`}
                aria-label="Toggle menu"
              >
                {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        <div
          className={`lg:hidden absolute top-full left-0 right-0 transition-all duration-300 ${
            theme === 'dark' 
              ? 'bg-black/98 backdrop-blur-lg border-b border-white/10' 
              : 'bg-white/98 backdrop-blur-lg border-b border-gray-200 shadow-lg'
          } ${isMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible pointer-events-none'}`}
        >
          <nav className="flex flex-col py-4 px-4 sm:px-6">
            {/* Mobile Contact Info */}
            <div className={`flex flex-col gap-2 pb-4 mb-4 border-b ${
              theme === 'dark' ? 'border-white/10' : 'border-gray-200'
            }`}>
              <a href={`mailto:${companyInfo.email}`} className={`flex items-center gap-2 text-sm ${
                theme === 'dark' ? 'text-white/60' : 'text-gray-600'
              }`}>
                <Mail size={14} />
                {companyInfo.email}
              </a>
              <a href={`tel:${companyInfo.phone}`} className={`flex items-center gap-2 text-sm ${
                theme === 'dark' ? 'text-white/60' : 'text-gray-600'
              }`}>
                <Phone size={14} />
                {companyInfo.phone}
              </a>
            </div>

            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.href}
                className={`py-3 text-lg font-medium transition-colors border-b ${
                  isActive(link.href)
                    ? theme === 'dark' ? 'text-[#00FFD1] border-[#00FFD1]/20' : 'text-[#00997A] border-[#00997A]/20'
                    : theme === 'dark' 
                      ? 'text-white/70 hover:text-white border-white/5' 
                      : 'text-gray-600 hover:text-gray-900 border-gray-100'
                }`}
              >
                {link.name}
              </Link>
            ))}
            <Link
              to="/contact"
              className={`mt-4 px-6 py-3 font-medium text-center text-lg transition-all duration-300 ${
                theme === 'dark'
                  ? 'bg-[#00FFD1] text-black hover:bg-[#00FFD1]/80'
                  : 'bg-[#00997A] text-white hover:bg-[#007A61]'
              }`}
            >
              Get Started
            </Link>
          </nav>
        </div>
      </header>
    </>
  );
};

export default Header;
